import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { CSVProcessRequest, ValidationResult } from "../types/api";
import { ColumnValidationWarning } from "./ColumnValidationWarning";

interface CSVProcessorProps {
  projectId: number;
  projectTitle: string;
  userRole?: string;
}

export const CSVProcessor: React.FC<CSVProcessorProps> = ({
  projectId,
  projectTitle,
  userRole = "researcher",
}) => {
  const { t } = useTranslation("csvProcessor");
  const [csvData, setCsvData] = useState("");
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const PREVIEW_ROWS = 5;

  // Pick the delimiter that yields the most fields on the header line,
  // preferring comma on ties. Mirrors the backend DetectDelimiter so a
  // ';'/tab/pipe file isn't parsed as a single column.
  const detectDelimiter = (headerLine: string): string => {
    let best = ",";
    let bestCount = 0;
    for (const d of [",", ";", "\t", "|"]) {
      const count = headerLine.split(d).length - 1;
      if (count > bestCount) {
        bestCount = count;
        best = d;
      }
    }
    return best;
  };

  const parseCSVLine = (line: string, delimiter: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCSVHeaders = (content: string): string[] => {
    const firstLine = content.split("\n")[0];
    if (!firstLine) return [];
    return parseCSVLine(firstLine, detectDelimiter(firstLine));
  };

  const parseCSVPreview = (
    csv: string,
  ): { headers: string[]; rows: string[][]; totalRows: number } => {
    const lines = csv.split("\n").filter((l) => l.trim());
    if (lines.length === 0) return { headers: [], rows: [], totalRows: 0 };
    const delimiter = detectDelimiter(lines[0]);
    const headers = parseCSVLine(lines[0], delimiter);
    const totalRows = lines.length - 1;
    const rows = lines
      .slice(1, 1 + PREVIEW_ROWS)
      .map((l) => parseCSVLine(l, delimiter));
    return { headers, rows, totalRows };
  };

  const handleFileSelect = (file: File) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError(t("errors.csvOnly"));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError(t("errors.fileTooLarge"));
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvData(content);
      setFileName(file.name);

      const headers = parseCSVHeaders(content);
      setAvailableColumns(headers);
      setSelectedColumns([]);
      setResult(null);
      setMessage(null);
      setValidationResult(null);
      setShowValidationWarning(false);
    };
    reader.readAsText(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const toggleColumn = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column],
    );
  };

  const selectAll = () => setSelectedColumns([...availableColumns]);
  const selectNone = () => setSelectedColumns([]);

  const handlePseudonymize = async () => {
    if (!csvData || selectedColumns.length === 0) {
      setError(t("errors.selectFileAndColumns"));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setMessage(null);
    setValidationResult(null);
    setShowValidationWarning(false);

    try {
      const request: CSVProcessRequest = {
        project_id: projectId,
        csv_data: csvData,
        columns: selectedColumns,
      };

      const validation = await apiService.validateCSV(request);
      setValidationResult(validation);

      if (!validation.valid) {
        setShowValidationWarning(true);
        setLoading(false);
        return;
      }

      const response = await apiService.pseudonymizeCSV(request);
      setResult(response.processed_csv);
      setMessage(response.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("errors.validationOrProcessingFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProceedWithWarnings = async () => {
    if (!csvData || selectedColumns.length === 0) return;

    setLoading(true);
    setShowValidationWarning(false);
    setError(null);

    try {
      const request: CSVProcessRequest = {
        project_id: projectId,
        csv_data: csvData,
        columns: selectedColumns,
      };

      const response = await apiService.pseudonymizeCSV(request);
      setResult(response.processed_csv);
      setMessage(
        `${response.message} ${t("messages.processedWithWarningsSuffix")}`,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.processingFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleColumnFix = (originalColumn: string, newColumn: string) => {
    setSelectedColumns((prev) =>
      prev.map((col) => (col === originalColumn ? newColumn : col)),
    );
    setShowValidationWarning(false);
    setValidationResult(null);
  };

  const handleCancelValidation = () => {
    setShowValidationWarning(false);
    setValidationResult(null);
  };

  const handleReverse = async () => {
    if (userRole !== "admin") {
      setError(t("errors.reverseNotAllowed"));
      return;
    }

    if (!csvData || selectedColumns.length === 0) {
      setError(t("errors.selectFileAndColumnsReverse"));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setMessage(null);

    try {
      const request: CSVProcessRequest = {
        project_id: projectId,
        csv_data: csvData,
        columns: selectedColumns,
      };

      const response = await apiService.reverseCSV(request);
      setResult(response.processed_csv);
      setMessage(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.reverseFailed"));
    } finally {
      setLoading(false);
    }
  };

  const downloadResult = () => {
    if (!result) return;

    const blob = new Blob([result], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "processed_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const resetFile = () => {
    setCsvData("");
    setAvailableColumns([]);
    setSelectedColumns([]);
    setFileName(null);
    setResult(null);
    setMessage(null);
    setError(null);
    setValidationResult(null);
    setShowValidationWarning(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!projectId) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>{t("noProjectMessage")}</p>
      </div>
    );
  }

  const rowCount = csvData
    ? csvData.split("\n").filter((line) => line.trim()).length - 1
    : 0;

  return (
    <div style={{ maxWidth: "800px", margin: "20px auto", padding: "20px" }}>
      <h2>{t("heading")}</h2>
      <p style={{ color: "#666", marginBottom: "20px" }}>
        {t("projectLabel")} <strong>{projectTitle}</strong>
      </p>

      {/* Drop zone / file upload */}
      {!csvData ? (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "4px",
            border: dragActive ? "2px dashed #007bff" : "2px dashed #dee2e6",
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              border: "1px dashed #ccc",
              borderRadius: "4px",
              backgroundColor: dragActive ? "#f0f8ff" : "white",
              cursor: "pointer",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <div style={{ fontSize: "16px" }}>
              {t("dropzone.clickOrDrag")}
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              {t("dropzone.hint")}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
      ) : (
        <>
          {/* File info bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
              border: "1px solid #dee2e6",
              marginBottom: "16px",
            }}
          >
            <div>
              <strong>{fileName}</strong>
              <span style={{ fontSize: "12px", color: "#666", marginLeft: "12px" }}>
                {t("fileInfo.summary", {
                  columns: availableColumns.length,
                  rows: rowCount,
                })}
              </span>
            </div>
            <button
              onClick={resetFile}
              style={{
                padding: "4px 12px",
                backgroundColor: "#95a5a6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              {t("fileInfo.changeFileButton")}
            </button>
          </div>

          {/* Column selection */}
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <strong>{t("columnSelection.heading")}</strong>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={selectAll}
                  style={{
                    padding: "2px 8px",
                    fontSize: "11px",
                    backgroundColor: "transparent",
                    border: "1px solid #007bff",
                    color: "#007bff",
                    borderRadius: "3px",
                    cursor: "pointer",
                  }}
                >
                  {t("columnSelection.selectAllButton")}
                </button>
                <button
                  onClick={selectNone}
                  style={{
                    padding: "2px 8px",
                    fontSize: "11px",
                    backgroundColor: "transparent",
                    border: "1px solid #95a5a6",
                    color: "#95a5a6",
                    borderRadius: "3px",
                    cursor: "pointer",
                  }}
                >
                  {t("columnSelection.clearButton")}
                </button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "6px",
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ddd",
                padding: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              }}
            >
              {availableColumns.map((column) => (
                <label
                  key={column}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column)}
                    onChange={() => toggleColumn(column)}
                    style={{ marginRight: "6px" }}
                  />
                  <span
                    style={{
                      color: selectedColumns.includes(column)
                        ? "#2ecc71"
                        : "#333",
                      fontWeight: selectedColumns.includes(column)
                        ? "bold"
                        : "normal",
                    }}
                  >
                    {column}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
              {t("columnSelection.selectedSummary", {
                selected: selectedColumns.length,
                total: availableColumns.length,
              })}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
            <button
              onClick={handlePseudonymize}
              disabled={loading || selectedColumns.length === 0}
              style={{
                padding: "8px 16px",
                backgroundColor:
                  selectedColumns.length > 0 && !loading
                    ? "#27ae60"
                    : "#bdc3c7",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor:
                  selectedColumns.length > 0 && !loading
                    ? "pointer"
                    : "not-allowed",
              }}
            >
              {loading
                ? t("actions.processing")
                : t("actions.deidentifyButton", {
                    count: selectedColumns.length,
                  })}
            </button>

            {userRole === "admin" && (
              <button
                onClick={handleReverse}
                disabled={loading || selectedColumns.length === 0}
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    selectedColumns.length > 0 && !loading
                      ? "#dc3545"
                      : "#bdc3c7",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    selectedColumns.length > 0 && !loading
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {loading ? t("actions.processing") : t("actions.reverseButton")}
              </button>
            )}
          </div>
        </>
      )}

      {/* Validation warning */}
      {showValidationWarning && validationResult && (
        <ColumnValidationWarning
          validationResult={validationResult}
          onColumnFix={handleColumnFix}
          onIgnoreWarning={handleProceedWithWarnings}
          onCancel={handleCancelValidation}
        />
      )}

      {/* Error message */}
      {error && (
        <div
          style={{
            color: "#e74c3c",
            backgroundColor: "#ffe6e6",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "20px",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {/* Success message */}
      {message && (
        <div
          style={{
            color: "#27ae60",
            backgroundColor: "#e6ffe6",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "20px",
            fontSize: "13px",
          }}
        >
          {message}
        </div>
      )}

      {/* Results */}
      {result && (() => {
        const preview = parseCSVPreview(result);
        return (
          <div style={{ marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <h3 style={{ margin: 0 }}>{t("results.heading")}</h3>
              <button
                onClick={downloadResult}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#27ae60",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("results.downloadButton")}
              </button>
            </div>

            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                overflow: "auto",
                maxHeight: "320px",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  fontFamily: "monospace",
                }}
              >
                <thead>
                  <tr>
                    {preview.headers.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "8px 10px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "2px solid #dee2e6",
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          fontWeight: "bold",
                          color: selectedColumns.includes(h)
                            ? "#27ae60"
                            : "#333",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid #eee",
                            whiteSpace: "nowrap",
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}>
              {preview.totalRows > PREVIEW_ROWS
                ? t("results.showingFirstCaption", {
                    shown: PREVIEW_ROWS,
                    total: preview.totalRows.toLocaleString(),
                  })
                : t("results.rowsCaption", { count: preview.totalRows })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};
